import {
	Box,
	debounce,
	Grid,
	InputAdornment,
	Stack,
	styled,
	TextField,
	Typography,
} from '@mui/material';

import { useCallback, useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import { ReactComponent as HighPriorityStatusIcon } from 'common/assets/images/dialHigh.svg';
import { ReactComponent as LowPriorityStatusIcon } from 'common/assets/images/dialLow.svg';
import { ReactComponent as MediumPriorityStatusIcon } from 'common/assets/images/dialMedium.svg';
import { ReactComponent as GridIcon } from 'common/assets/images/grid.svg';
import { ReactComponent as OpenTasksIcon } from 'common/assets/images/open-tasks-dashboard-icon.svg';
import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';
import { ReactComponent as SortIcon } from 'common/assets/images/sort-icon.svg';
import { ReactComponent as StatusTypeIcon } from 'common/assets/images/status-type-circle.svg';
import { ReactComponent as TableIcon } from 'common/assets/images/table.svg';
import CardTable from 'common/components/CardTable/CardTable';
import { Loader } from 'common/components/Loader/Loader';
import { Column } from 'common/components/Table/CommonTableStyles';
import TableComponent from 'common/components/Table/Table';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import {
	ToolbarType,
	ViewType,
	dashboardOverviewTools,
	TABLE_VIEW_PAGE_SIZE,
	GRID_VIEW_PAGE_SIZE,
	MODULES,
	MODULE_NAME,
} from 'common/constants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { TASK_PRIORITY_LEVEL_STATUS } from 'common/constants/TaxonomyConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setLandingPageSearchText,
	setTaskListViewType,
	setLandingPageTableClearData,
	setTaskRenderStatus,
	setTaskRowData,
} from 'common/store/reducer/commonReducer';
import { setHorizonLandingSearchText } from 'common/store/reducer/horizonScanReducer';
import { isModuleExists } from 'common/utils/authCheck';
import {
	dateFullYearFormat,
	pascalFormat,
	getGridPageSize,
	getTablePageSize,
	isModuleEnabled,
} from 'common/utils/utilFunctions';

import { useFeatureFlag } from 'hooks/useFeatureFlags';
import useTableQuery from 'hooks/useTableQuery';

import FilterDropdown from 'features/HomePage/TasksList/AdvanceFilterDropdown';

import { getAllMyTasks } from '../api';
import { ListOfMetricsValue } from '../api/type-def';

import MetricsCards from './MetricsCard/MetricsCard';
import TasksCard from './TasksCard';
import {
	navigateToHorizonChange,
	navigateToReqInventory,
	navigateToRiskRefTaxonomy,
} from './TasksListUtils';

import styles from './TasksList.module.scss';

const {
	searchFieldBackground,
	searchFieldBorderRadius,
	chipRedColor,
	chipWarningColor,
	chipSucessColor,
	chipPriorityLow,
	chipPriorityHigh,
	chipPriorityMedium,
} = styles;

const {
	HORIZON_SCAN_NAME,
	REQUIREMENT_INVENTORY_NAME,
	RISK_REFERENCE_TAXONOMY_NAME,
} = MODULE_NAME;
const { OK } = HTTP_STATUS_CODES;
const { REQUIREMENT_INVENTORY } = MODULES;
const { TABLE_SIZE_12, TABLE_SIZE_24, TABLE_SIZE_48 } = TABLE_VIEW_PAGE_SIZE;
const { GRID_SIZE_10, GRID_SIZE_25, GRID_SIZE_50 } = GRID_VIEW_PAGE_SIZE;
const { PRIORITY_LOW, PRIORITY_MEDIUM, PRIORITY_HIGH } =
	TASK_PRIORITY_LEVEL_STATUS;
const { SEARCH, SORT, FILTER, TOGGLE_VIEW } = ToolbarType;
const { GRID, TABLE } = ViewType;

const StyledSearchTextField = styled(TextField)({
	background: searchFieldBackground,
	borderRadius: searchFieldBorderRadius,
	width: '100%',
});

const TasksList = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const selectedViewType = useSelector(
		(state: RootState) => state.common.taskListViewType,
	);

	const isRequirementInventoryEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_REGULATION_INVENTORY_ENABLED,
	);
	const isReqInvEnabled = isModuleEnabled(
		isRequirementInventoryEnabled,
		isModuleExists(REQUIREMENT_INVENTORY),
	);
	const [, setError] = useState<boolean>(false);
	const [toastMessage, setToastMessageLocal] = useState('');
	const [isToastOpen, setIsToastOpen] = useState(false);
	const [, setTasksData] = useState<any>([]);
	const [, setCardPageSize] = useState<number>(12);
	const [sortChange, setSortChange] = useState<any>();
	const [pageChange, setPageChange] = useState<any>();
	const [metricsData, setMetricsData] = useState<ListOfMetricsValue[]>([]);
	const [viewType, setViewType] = useState<ViewType>(selectedViewType);
	const overviewTools = dashboardOverviewTools;
	const landingPageTableData = useSelector(
		(state: RootState) => state.common.landingPageTableData,
	);

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const landingPageSearchText = useSelector(
		(state: RootState) => state.common.landingPageSearchText,
	);

	const taskRenderStatus = useSelector(
		(state: RootState) => state.common.taskRenderStatus,
	);

	const { tableQueryObj: tableProps, updateTableQuery } = useTableQuery({
		id: 'HomePageTaskListTable',
	});

	const handleToggleViewType = (type: ViewType) => {
		dispatch(setTaskRenderStatus(true));
		setViewType(type);
		dispatch(setTaskListViewType(type));
	};

	const getIconClass = (targetType: ViewType) =>
		[
			styles.icon,
			viewType === targetType ? styles.active : styles.inactive,
		].join(' ');
	const handleToast = useCallback(() => {
		setIsToastOpen(false);
		setToastMessageLocal('');
	}, []);
	const getPriorityStatusIcon = (type: string) => {
		const statusType = type !== '' ? type.toLowerCase() : '';
		let imgElem: any;
		switch (statusType) {
			case PRIORITY_LOW.toLowerCase():
				imgElem = <StatusTypeIcon fill={chipSucessColor} />;
				break;
			case PRIORITY_MEDIUM:
				imgElem = <StatusTypeIcon fill={chipWarningColor} />;
				break;
			case PRIORITY_HIGH:
				imgElem = <StatusTypeIcon fill={chipRedColor} />;
				break;
			default:
				break;
		}

		return imgElem;
	};
	const getRequirementInventoryClass = (params: any) => {
		let reqEnvClass = styles['taskslist-table-risk-name'];
		if (params.moduleName === REQUIREMENT_INVENTORY_NAME) {
			if (!isReqInvEnabled) {
				reqEnvClass = styles['taskslist-table-risk-name-linkdisabled'];
			}
		}
		return reqEnvClass;
	};

	const navigateToEntityPage = (params: any) => {
		dispatch(setTaskRowData(params));
		// navigations based on module name
		switch (params?.moduleName?.toLowerCase()) {
			case REQUIREMENT_INVENTORY_NAME.toLowerCase():
				navigateToReqInventory(params, dispatch, isReqInvEnabled, navigate);
				break;
			case HORIZON_SCAN_NAME?.toLowerCase():
				navigateToHorizonChange(params, dispatch, navigate);
				break;
			case RISK_REFERENCE_TAXONOMY_NAME?.toLowerCase():
				navigateToRiskRefTaxonomy(params, dispatch, navigate);
				break;
			default:
				break;
		}
	};

	const columns: readonly Column[] = [
		{
			id: 'taskName',
			label: 'Name',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<Typography
					component="div"
					data-testid="task-link"
					className={getRequirementInventoryClass(params)}
					onClick={() => navigateToEntityPage(params)}
				>
					{params?.taskName}
				</Typography>
			),
		},
		{
			id: 'assignedObjectType',
			label: 'Module',
			disablePadding: false,
			render: (params: any) => (
				<Typography
					component="div"
					data-testid="task-object-link"
					className={styles['task-list-object-type']}
				>
					{pascalFormat(params?.moduleName)}
				</Typography>
			),
		},
		{ id: 'taskType', label: 'Type', disablePadding: false },
		{ id: 'status', label: 'Status', disablePadding: false, isSortable: true },
		{
			id: 'priorityLevel',
			label: 'Priority',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<div className={styles['tasks-list-prioritylevel']}>
					<Typography component="div" data-testid="status">
						{params?.priorityLevel
							? getPriorityStatusIcon(params?.priorityLevel)
							: getPriorityStatusIcon('')}
					</Typography>
					<Typography component="div" data-testid="priority-level">
						{pascalFormat(params?.priorityLevel)}
					</Typography>
				</div>
			),
		},
		{
			id: 'dueDate',
			label: 'Due Date',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<Typography>{dateFullYearFormat(params?.dueDate)}</Typography>
			),
		},
	];
	useEffect(() => {
		if (landingPageTableData) {
			setTasksData(landingPageTableData?.records);
		}
	}, [landingPageTableData]);

	const getAllMyTasksData = async (data: any = {}, text = '') => {
		setError(false);
		setTasksData([]);
		const paramsData = { ...data };
		paramsData.userId = loggedInUser?.userId ?? '';
		paramsData.filterText = text;
		const response: any = await getAllMyTasks(paramsData);

		if (response?.statusCode === OK) {
			const responseData = response?.responseBody;
			setListOfMetrics(responseData?.userTaskStatusList);
		} else {
			setError(true);
			setToastMessageLocal(APP_GENERAL_ERROR_MSG);
		}
	};

	const handlePageChange = (data: any, flag?: boolean) => {
		if (flag) {
			updateTableQuery(data);
			setPageChange(data);
		}
	};

	const handleCardPageChange = (data: any) => {
		setPageChange(data);
		updateTableQuery(data);
	};
	const handleSortChange = (data: any) => {
		setSortChange(data);
	};

	const setListOfMetrics = (data: any) => {
		const respData: ListOfMetricsValue[] = [
			{
				label: 'Open tasks',
				count: 0,
				highPriority: 0,
				key: 'openTasks',
				imgLink: <OpenTasksIcon />,
			},
			{
				label: 'High priority',
				count: 0,
				highPriority: 0,
				key: 'highPriority',
				imgLink: <HighPriorityStatusIcon fill={chipPriorityHigh} />,
			},
			{
				label: 'Medium priority',
				count: 0,
				highPriority: 0,
				key: 'mediumPriority',
				imgLink: <MediumPriorityStatusIcon fill={chipPriorityMedium} />,
			},
			{
				label: 'Low priority',
				count: 0,
				highPriority: 0,
				key: 'lowPriority',
				imgLink: <LowPriorityStatusIcon fill={chipPriorityLow} />,
			},
		];

		if (data) {
			const updatedRespData = respData.map((element) => {
				const matchingElement = data.find(
					(item: { status: string }) =>
						item?.status?.toLowerCase() === element?.key?.toLowerCase(),
				);
				return {
					label: element.label,
					count: matchingElement ? matchingElement.count : 0,
					imgLink: element.imgLink,
				};
			});

			setMetricsData(updatedRespData);
		} else {
			setMetricsData(respData);
		}
	};

	const changeSearchInput = (e: any) => {
		const pageObj = {
			pageSize: selectedViewType === TABLE ? TABLE_SIZE_12 : GRID_SIZE_10,
			pageNumber: 0,
		};
		updateTableQuery(pageObj);
		const text = e?.target.value;
		dispatch(setHorizonLandingSearchText(text));
		setPageChange(pageObj);
		dispatch(setLandingPageSearchText(text));
	};
	const debouncedSearchHandler = debounce((e) => {
		changeSearchInput(e);
	}, 500);

	useEffect(() => {
		const paramsData = { ...tableProps };
		if (viewType === TABLE) {
			if (
				tableProps?.pageSize === TABLE_SIZE_12 ||
				tableProps?.pageSize === TABLE_SIZE_24 ||
				tableProps?.pageSize === TABLE_SIZE_48
			) {
				setCardPageSize(tableProps?.pageSize);
			} else {
				const size = getTablePageSize(tableProps?.pageSize);
				paramsData.pageSize = size;
				setCardPageSize(size);
			}
			setPageChange(paramsData);
			updateTableQuery(paramsData);
		} else {
			if (
				tableProps?.pageSize === GRID_SIZE_10 ||
				tableProps?.pageSize === GRID_SIZE_25 ||
				tableProps?.pageSize === GRID_SIZE_50
			) {
				setPageChange(tableProps?.pageSize);
			} else {
				const size = getGridPageSize(tableProps?.pageSize);
				paramsData.pageSize = size;
				setPageChange(paramsData);
			}
			updateTableQuery(paramsData);
		}

		return () => {
			dispatch(setTaskListViewType(viewType));
		};
	}, [viewType]);

	useEffect(
		() => () => {
			// to clean up the data on unmount
			dispatch(setTaskRenderStatus(true));
			dispatch(setLandingPageTableClearData());
		},
		[viewType],
	);

	useEffect(() => {
		getAllMyTasksData(tableProps);
	}, [tableProps]);

	const isHomePageSearchEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_HOME_PAGE_SEARCH_ENABLED,
	);
	const isHomePageFilterEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_HOME_PAGE_FILTER_ENABLED,
	);

	const taskDataCleanUpHandler = () => {
		dispatch(setLandingPageTableClearData());
	};

	return (
		<Grid
			container
			className={styles['taskslist-metrics']}
			data-testid="tasks-list-container"
		>
			<Grid item xs={12} mb={2}>
				<div className={styles['taskslist-heading']}>My tasks</div>
			</Grid>
			<Grid item xs={12} mb={2}>
				<div className={styles['taskslist-metrics-container']}>
					{metricsData.map((res) => (
						<MetricsCards
							key={res.label}
							label={res.label}
							count={res.count}
							imgLink={res.imgLink}
						/>
					))}
				</div>
			</Grid>
			<Grid item xs={12}>
				<Stack
					direction="row"
					gap={1}
					alignItems="center"
					alignSelf="flex-end"
					justifyContent="flex-end"
				>
					{isHomePageSearchEnabled && overviewTools.includes(SEARCH) && (
						<StyledSearchTextField
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchOutlined className={styles['input-search-icon']} />
									</InputAdornment>
								),
								inputProps: { 'data-testid': 'searchInput' },
							}}
							className={styles['search-input']}
							fullWidth={false}
							placeholder="Search"
							size="small"
							onChange={debouncedSearchHandler}
							defaultValue={landingPageSearchText}
						/>
					)}

					{isHomePageFilterEnabled && overviewTools.includes(SORT) && (
						<div className={styles.btn}>
							<SortIcon />
						</div>
					)}

					{isHomePageFilterEnabled && overviewTools.includes(FILTER) && (
						<div className={styles.btn}>
							<FilterDropdown
								sortChange={sortChange}
								pageChange={pageChange}
								selectedViewType={tableProps}
								cleanTaskData={taskDataCleanUpHandler}
							/>
						</div>
					)}

					{overviewTools.includes(TOGGLE_VIEW) && (
						<div className={styles['toggle-view']}>
							<Box
								data-testId="grid-btn"
								onClick={() => handleToggleViewType(GRID)}
								className={getIconClass(GRID)}
							>
								<TableIcon />
							</Box>
							<Box
								data-testId="table-btn"
								onClick={() => handleToggleViewType(TABLE)}
								className={getIconClass(TABLE)}
							>
								<GridIcon />
							</Box>
						</div>
					)}
				</Stack>
			</Grid>

			{viewType === 'GRID' ? (
				<Grid item xs={12} mt={3} data-testid="table-grid-container">
					<TableComponent
						defaultQuery={tableProps}
						rows={landingPageTableData?.records}
						columns={columns}
						totalItems={landingPageTableData?.totalRecords}
						defaultSortKey=""
						onPageHandler={handlePageChange}
						isLoading={taskRenderStatus}
						tableStyles={{ minWidth: '46.875em', maxHeight: '27.5em' }}
						totalPagesCount={landingPageTableData?.totalPages}
						onSortHandler={handleSortChange}
						enableHeaderColor
						isRenderPagination
					/>
				</Grid>
			) : (
				<Grid item xs={12} mt={3}>
					{!taskRenderStatus ? (
						<Grid
							item
							container
							spacing={3}
							data-testid="task-list-card-container"
						>
							{landingPageTableData?.records &&
							landingPageTableData?.records?.length > 0 ? (
								<CardTable
									id="taskslist"
									columns={3}
									handlePagination={handleCardPageChange}
									queryPageSize={tableProps?.pageSize}
									rows={landingPageTableData?.records}
									totalItems={landingPageTableData.totalRecords}
									showSelectPageOption
									defaultQuery={tableProps}
									render={(rowData: any) => (
										<TasksCard
											cardKey={rowData?.id}
											data={rowData}
											navigateToPath={navigateToEntityPage}
										/>
									)}
								/>
							) : (
								<Box className={styles['taskslist-cards-loader']}>
									{' '}
									No data found
								</Box>
							)}
						</Grid>
					) : (
						<div
							data-testid="taskslist-cards-loader"
							className={styles['taskslist-cards-loader']}
						>
							<Loader show />
						</div>
					)}
				</Grid>
			)}

			<AlertComponent
				open={isToastOpen}
				toastMessage={toastMessage}
				severity="error"
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</Grid>
	);
};

export default TasksList;
